<template>
  <div
    :data-testid="testId"
    class="
      tw-h-full
      tw-flex
      tw-border
      tw-border-sf-neutral-lighter-2
      tw-border-solid
      tw-bg-white
      tw-rounded-lg
      tw-overflow-hidden
      tw-max-w-condensed-content
      tw-mx-auto
    "
  >
    <div class="tw-flex tw-flex-1 tw-flex-col">
      <MeetingPreparationHeader
        :empty="empty"
        :suggestions-enabled="suggestionsEnabled"
        :show-sidebar="showSidebar"
        :talking-point-tag-id="talkingPointTag?.id"
        :ledger-id="ledgerId"
        :ledger-label="ledgerLabel"
        @toggle-sidebar="toggleSidebar"
        @complete-meeting="completeMeeting"
      />
      <div
        class="tw-py-4 tw-h-full tw-overflow-y-auto"
        data-testid="meeting-preparation-remarks"
      >
        <div v-if="loading">
          <RemarkV2Skeleton v-for="i in 4" :key="i" class="tw-mx-2 tw-my-1" />
        </div>
        <MeetingPreparationEmptyState
          v-else-if="empty"
          :talking-point-tag="talkingPointTag"
        />
        <div v-else class="tw-flex tw-flex-col">
          <div
            v-if="unorderedRemarks.length"
            class="tw-relative"
            data-testid="meeting-unordered-remarks"
          >
            <div class="tw-my-5 tw-mx-8 tw-h-px tw-bg-sf-danger" />
            <div
              class="
                tw-absolute
                tw-top-0
                tw-left-1/2
                tw--translate-x-1/2
                tw-flex
                tw-flex-row
                tw-items-center
                tw-gap-1
                tw-h-8
                tw-my-1
                tw-rounded
                tw-px-2
                tw-bg-sf-neutral-subtle-2
                tw-border
                tw-border-solid
                tw-border-sf-danger/20
              "
            >
              <span class="tw-text-sf-neutral-darker-1 tw-font-medium">
                {{ I18n.t('client_meetings.meeting_preparation.new_messages') }}
              </span>
              <SfButtonInverted
                small
                data-testid="meeting-dismiss-new-button"
                @click="joinUnordered"
              >
                <span class="tw-text-sf-neutral">
                  {{ I18n.t('client_meetings.meeting_preparation.ok') }}
                </span>
              </SfButtonInverted>
            </div>
            <Draggable
              v-model="unorderedRemarks"
              item-key="id"
              group="remarks"
              handle=".handle"
              class="tw-flex tw-flex-col tw-gap-4"
              ghost-class="ghost-draggable-remark"
              :animation="200"
              @start="setDragging(true)"
              @end="setDragging(false)"
            >
              <template #item="{ element: remark }">
                <DraggableRemark
                  :remark="remark"
                  :dragging="dragging"
                  handle-class="handle"
                />
              </template>
            </Draggable>
            <div class="tw-my-5 tw-mx-8 tw-h-px tw-bg-sf-danger" />
          </div>
          <Draggable
            v-model="orderedRemarks"
            data-testid="meeting-ordered-remarks"
            item-key="id"
            group="remarks"
            handle=".handle"
            class="tw-flex tw-flex-col tw-gap-4"
            :class="orderedRemarksClass"
            ghost-class="ghost-draggable-remark"
            :animation="200"
            @start="setDragging(true)"
            @end="setDragging(false)"
          >
            <template #item="{ element: remark }">
              <DraggableRemark
                :remark="remark"
                :dragging="dragging"
                handle-class="handle"
              />
            </template>
          </Draggable>
        </div>
      </div>
    </div>
    <MeetingPreparationSidebar
      v-if="showSidebar"
      :suggestions="suggestions"
      @toggle-sidebar="toggleSidebar"
    />
  </div>
  <complete-meeting-modal :remark-ids="remarkIds" />
</template>

<script setup>
import { computed, defineProps, onBeforeUnmount, onMounted, ref } from 'vue'
import { useStore } from 'vuex'
import { orderBy } from 'lodash'
import Draggable from 'vuedraggable'
import { storeToRefs } from 'pinia'

import DraggableRemark from '@app/application/client_meetings/screens/preparation/components/draggable-remark.vue'
import MeetingPreparationEmptyState from './meeting-preparation-empty-state.vue'
import MeetingPreparationHeader from '@app/application/client_meetings/screens/preparation/components/meeting-preparation-header.vue'
import MeetingPreparationSidebar from './meeting-preparation-sidebar.vue'
import RemarkV2Skeleton from '@app/shared/vue/components/molecules/remark-v2/remark-v2-skeleton.vue'
import CompleteMeetingModal from '@app/application/client_meetings/screens/preparation/components/complete-meeting-modal.vue'
import { SfButtonInverted } from '@app/shared/vue/components/sf-button'

import useOrderedRemarks from '@app/application/client_meetings/screens/preparation/composables/ordered-remarks'
import { useAssistantSuggestionsStore } from '@app/application/client_meetings/screens/preparation/store/assistant-suggestions'
import useRemarkTags from '@app/shared/vue/composables/use-remark-tags'
import useOpenRemarkModal from '@app/shared/vue/composables/use-open-remark-modal'

import scrollHelper from '@app/application/communication/sections/communication/components/mixins/scroll-helper.js'

import { getSafeJSONValue, setJSONValue } from '@app/utils/local-storage'

const store = useStore()
const remarkTags = useRemarkTags()

const suggestionStore = useAssistantSuggestionsStore()
const { suggestions } = storeToRefs(suggestionStore)

const props = defineProps({
  initialOrder: {
    type: Array,
    default: () => [],
    required: false,
  },
  ledgerId: {
    type: Number,
    required: true,
  },
  ledgerLabel: {
    type: String,
    required: true,
  },
  suggestionsEnabled: {
    type: Boolean,
    required: true,
  },
})

defineOptions({
  mixins: [scrollHelper],
  methods: {
    handleAnchorScroll(callback) {
      const anchorSelector = this.elementAnchorLink?.()

      if (!anchorSelector) return

      const container = document.querySelector(
        '[data-testid="meeting-preparation-remarks"]'
      )
      const target = container?.querySelector(anchorSelector)

      if (!container || !target) return

      const scrollOffset = target.offsetTop - container.offsetTop

      container.scrollTo({
        top: scrollOffset,
        behavior: 'smooth',
      })

      this.anchorElementScrolled = true

      if (callback) {
        callback()
      }
    },
  },
})

const talkingPointTag = computed(() => store.getters.talkingPointTag)
const remarks = computed(() => {
  return orderBy(
    Object.values(store.state.remarks.remarks),
    'sortTimestamp',
    'desc'
  )
})

const { orderedRemarks, unorderedRemarks, allRemarks, setOrder, settingOrder } =
  useOrderedRemarks(remarks, props.initialOrder)

const orderedRemarksClass = computed(() => {
  if (unorderedRemarks.value.length > 0) {
    return 'tw-opacity-50'
  }
  return ''
})

function joinUnordered() {
  setOrder(allRemarks.value.map((r) => r.id))
}

const dragging = ref(false)
function setDragging(newValue) {
  dragging.value = newValue
}

const empty = computed(() => remarks.value.length === 0)
const loading = computed(
  () => empty.value && store.getters.fetchingCommunicationsInProgress
)
const testId = computed(() =>
  settingOrder.value
    ? 'meeting-preparation-setting-order'
    : 'meeting-preparation-idle'
)

const remarkIds = computed(() => allRemarks.value.map((remark) => remark.id))

async function refreshCommunicationData(data = {}) {
  await store.dispatch('fetchCommunication')

  if (data.action === 'createRemark') {
    window.eventHub.$emit('highlightRemark', {
      elementId: `remark-${data.remark.id}`,
      force: true,
    })

    if (data.callbackPayload && data.callbackPayload.suggestionText) {
      suggestionStore.removeSuggestion(data.callbackPayload.suggestionText)
      if (suggestionStore.suggestionCount === 0) {
        suggestionStore.fetchSuggestions()
      }
    }
  }

  if (Object.keys(data).length !== 0) {
    joinUnordered()
  }
}
store.dispatch('fetchMentionUsernames')

async function onCreated() {
  refreshCommunicationData()
  if (props.suggestionsEnabled) {
    await suggestionStore.fetchSuggestions()
  }
}

onCreated()

onMounted(() => {
  window.eventHub.$on('refreshCommunicationData', refreshCommunicationData)
  window.eventHub.$on('add-suggested-talking-point', addSuggestedTalkingPoint)
})

onBeforeUnmount(() => {
  window.eventHub.$off('refreshCommunicationData', refreshCommunicationData)
  window.eventHub.$off('add-suggested-talking-point', addSuggestedTalkingPoint)
})

const showSidebarSetting = getSafeJSONValue('showMeetingPreparationSidebar')
const showSidebar = ref(
  showSidebarSetting === null || showSidebarSetting === undefined
    ? props.suggestionsEnabled
    : showSidebarSetting && props.suggestionsEnabled
)

function toggleSidebar() {
  showSidebar.value = !showSidebar.value
  setJSONValue('showMeetingPreparationSidebar', showSidebar.value)
}

function completeMeeting() {
  window.modalCitizen.openModalPortal('complete-meeting-modal')
}

const selectedTagIds = computed(() =>
  talkingPointTag.value ? [talkingPointTag.value?.id] : []
)

const openModal = useOpenRemarkModal({
  ownerType: 'Company',
  ownerId: window.Silverfin.currentCompanyId,
  ledgerId: props.ledgerId,
  ledgerLabel: props.ledgerLabel,
  remarkText: true,
  hidePeriodSelector: true,
  remarkTags: remarkTags.value,
  selectedTagIds: selectedTagIds,
})

function addSuggestedTalkingPoint(text) {
  const callbackPayload = {
    suggestionText: text,
  }

  openModal(text, callbackPayload)
}
</script>
